import type { WorkflowDeclaration, WorkflowTone } from '@nexus/core';
import type { Status } from '@nexus/ui';
import { queryOptions } from '@tanstack/react-query';
import { api } from '../api/client';
import type { SessionInfo } from '../api/types';
import type { WebModule } from '../modules/define';
import { useModules } from '../modules/context';
import { canIn, useSession } from '../session/session';

/** State of a record as `GET /api/workflows` returns it. */
export interface WorkflowView {
  state: string;
  enteredAt: string;
  enteredBy: string | null;
  version: number;
  /** Transitions the person may take now. */
  transitions: string[];
  steps: {
    id: string;
    transition: string;
    from: string;
    to: string;
    actor: { id: string; displayName: string | null; erased?: boolean } | null;
    reason: string | null;
    at: string;
  }[];
}

/** An entry of `GET /api/workflows/approvals`. */
export interface ApprovalItem {
  entity: string;
  recordId: string;
  title: string | null;
  state: string;
  enteredAt: string;
  requestedBy: { id: string; displayName: string | null } | null;
  transitions: string[];
  path: string;
}

export function workflowQuery(entity: string, recordId: string) {
  return queryOptions({
    queryKey: ['workflows', entity, recordId],
    queryFn: ({ signal }) =>
      api.get<WorkflowView>(`/workflows?${new URLSearchParams({ entity, recordId }).toString()}`, {
        signal,
      }),
  });
}

export const approvalsQuery = queryOptions({
  queryKey: ['workflows', 'approvals'],
  queryFn: ({ signal }) =>
    api.get<{ items: ApprovalItem[]; more: boolean }>('/workflows/approvals', { signal }),
});

export function useWorkflowDeclarations(): WorkflowDeclaration[] {
  return useModules().flatMap((m) => m.manifest.workflows ?? []);
}

export function useWorkflowDeclaration(entity: string): WorkflowDeclaration {
  const declaration = useWorkflowDeclarations().find((w) => w.entity === entity);
  if (!declaration) throw new Error(`No workflow is declared for "${entity}"`);
  return declaration;
}

/** Whether some active module has decisions the person may take: shows "Für mich". */
export function canApproveIn(modules: readonly WebModule[], session: SessionInfo): boolean {
  return modules
    .filter((m) => session.modules.includes(m.manifest.id))
    .some((m) =>
      (m.manifest.workflows ?? []).some((w) =>
        w.transitions.some((t) => t.approval && canIn(session, t.permission)),
      ),
    );
}

export function useCanApprove(): boolean {
  return canApproveIn(useModules(), useSession());
}

export const TONE_STATUS: Record<WorkflowTone, Status> = {
  neutral: 'neutral',
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
};
